import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { t, Trans } from '@lingui/macro';
import _ from 'lodash';
import { Button, MenuItem } from '@material-ui/core';
import { i18n } from '@lingui/core';

import FormSection from '../../form/FormSection';
import NgpForm from '../../form/NgpForm';
import { MANUAL_ERROR_TYPE, USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import { useErrorWrapper } from '../../../services/FormErrorService';
import { useFormQueryService, useFindById } from '../../../services/FormQueryService';
import AdminPage from '../AdminPage';
import Actions from '../../page-framework/actions/Actions';
import { useFormSaveService, useFormSubmitWrapper } from '../../../services/FormSaveService';
import { useFormDeleteService, useFormDeleteWrapper } from '../../../services/FormDeleteService';
import useBreadcrumb from '../../breadcrumb/UseBreadcrumb';
import ConfirmDeleteButton from '../../form/ConfirmDeleteButton';
import {
    AGENTS_FIELD,
    AUTH_CERTIFICATE_FIELD,
    DESCRIPTION_FIELD,
    EAP_TYPE_FIELD,
    EAP_TYPE_OPTIONS,
    IS_DEFAULT_FIELD,
    PROFILE_NAME_FIELD,
    ODDITIONAL_NAME_FIELD,
    PSK_FIELD,
    USERNAME_FIELD,
    AUTH_CERTIFICATE_DEFAULT_OPTION,
} from './WiredNetworksConstants';
import NgpTextField from '../../form/NgpTextField';
import NgpTextarea from '../../form/NgpTextarea';
import NgpSelect from '../../form/NgpSelect';
import NgpCheckbox from '../../form/NgpCheckbox';
import NgpPasswordField from '../../form/NgpPasswordField';
import { AdminPages, buildPageId, useEntityIdParam } from '../AdminLinkUtils';
import { PASSWORD_FIELD } from '../users/UserConstants';
import { CERTIFICATE_NAME_FIELD, CERTIFICATE_USAGES, ASSOCIATION_TYPE } from '../certificates/CertificateConstants';
import { errorAlertObject, ID_FIELD } from '../../form/FormConstants';
import { HTTP_STATUS_400, throwValidationError } from '../../../services/ApiDataService';
import WiredNetworksAgents from './WiredNetworksAgents';
import useUiAlerts, { createAlertConfig, ERROR } from '../../../stores/AlertsStore';

const functionName = AdminPages.WiredNetworks;
const goToUrl = '../';
const baseUrl = '/ipm/v1/admin/wiredNetworks/';
const certsAPIUrl = '/ipm/v1/admin/nPointCertificate';
const defaultValues = {
    [PROFILE_NAME_FIELD]: '',
    [ODDITIONAL_NAME_FIELD]: '',
    [DESCRIPTION_FIELD]: '',
    [EAP_TYPE_FIELD]: EAP_TYPE_OPTIONS.peapMschapV2,
    [IS_DEFAULT_FIELD]: false,
    [AUTH_CERTIFICATE_FIELD]: AUTH_CERTIFICATE_DEFAULT_OPTION,
};

const passcodeInvalidErrorText = i18n._(t`The pre-shared key must be 8-63 ASCII characters or 64 hexadecimal digits.`);

function isPasscodeValid(psk) {
    const rawRegex = /^[\x20-\x7F]+$/;
    const preHashedRegex = /^[a-f0-9]+$/i;
    if (!_.isEmpty(psk)) {
        return (
            (psk.length >= 8 && psk.length <= 63 && rawRegex.test(psk)) ||
            (psk.length === 64 && preHashedRegex.test(psk))
        );
    }

    return true;
}

export default function WiredNetworksDetail() {
    const recordId = useEntityIdParam();
    const formDeleteService = useFormDeleteService();
    const formSaveService = useFormSaveService();
    const formQueryService = useFormQueryService();
    const { submitWrapper, saving } = useFormSubmitWrapper();
    const { deleteWrapper, deleting } = useFormDeleteWrapper();
    const { errorWrapper } = useErrorWrapper();
    const methods = useForm(USE_FORM_DEFAULT_OPTIONS);
    const { reset, setError, handleSubmit, getValues, trigger } = methods;
    const functionBreadCrumbs = [{ link: goToUrl, text: i18n._(t`Wired Networks`) }];
    const newBreadCrumbText = t`New Wired Network`;
    const [, uiAlertActions] = useUiAlerts();

    const [agentSelectionIsDirty, setAgentSelectionIsDirty] = useState(false);
    const [showDeleteButton, setShowDeleteButton] = useState(false);
    const [requiresAuth, setRequiresAuth] = useState(true);
    const [requiresCertificate, setRequiresCertificate] = useState(false);
    const [requiresPassword, setRequiresPassword] = useState(true);
    const [certificates, setCertificates] = useState([]);

    useEffect(() => {
        formQueryService
            .queryAPI(certsAPIUrl, {
                certificateUsage: { $eq: CERTIFICATE_USAGES.wired },
                associationType: { $eq: ASSOCIATION_TYPE.shared },
            })
            .then((wiredCerts) => {
                const certs = _.sortBy(wiredCerts, [CERTIFICATE_NAME_FIELD]);
                certs.unshift({
                    [CERTIFICATE_NAME_FIELD]: AUTH_CERTIFICATE_DEFAULT_OPTION,
                    [ID_FIELD]: AUTH_CERTIFICATE_DEFAULT_OPTION,
                });
                return certs;
            })
            .then(setCertificates);
        // Only want to run once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Post handling of data coming back from the server
     * @param response
     * @returns {*}
     */
    const postFindByIdHandler = (response) => {
        setShowDeleteButton(true);

        if (_.isEmpty(_.get(response, AUTH_CERTIFICATE_FIELD))) {
            _.set(response, AUTH_CERTIFICATE_FIELD, AUTH_CERTIFICATE_DEFAULT_OPTION);
        }

        const builtRecord = _.merge(_.pick(defaultValues, [EAP_TYPE_FIELD, AUTH_CERTIFICATE_FIELD]), response);

        handleEAPTypeChange(builtRecord[EAP_TYPE_FIELD]);

        return builtRecord;
    };

    const { initialized, finding, formData } = useFindById({
        url: baseUrl,
        recordId,
        postFindByIdHandler,
        resetFunc: reset,
        defaultValues,
        errorHandler: errorWrapper({}, goToUrl),
    });
    const breadcrumbs = useBreadcrumb(functionBreadCrumbs, recordId, formData, PROFILE_NAME_FIELD, newBreadCrumbText);

    const handleFormSubmit = (dataToSave = {}) => {
        if (!isPasscodeValid(getValues(PSK_FIELD))) {
            setError(PSK_FIELD, {
                type: MANUAL_ERROR_TYPE,
                message: passcodeInvalidErrorText,
            });
            return throwValidationError();
        }
        // If dataToSave.isDefault is true and dataToSave does not have the 'agents' property,
        // explicitly add 'agents' to ensure database updates behave as expected.
        // This prevents inconsistencies between DB table Ssid and Agents when updating records.
        if (dataToSave.isDefault && !_.has(dataToSave, AGENTS_FIELD)) {
            _.set(dataToSave, AGENTS_FIELD, formData[AGENTS_FIELD]);
        }

        if (getValues(AUTH_CERTIFICATE_FIELD) === AUTH_CERTIFICATE_DEFAULT_OPTION) {
            _.set(dataToSave, AUTH_CERTIFICATE_FIELD, '');
        }

        return formSaveService.save({
            url: baseUrl,
            recordId: formData._id,
            dataToSave: _.extend(dataToSave, { _id: formData._id }),
            setError,
        });
    };

    const deleteErrorHandler = (errorObject) => {
        // We may have handled errors at this point, but leaving here in case we want
        // to separate error handing.
        uiAlertActions.createAlert(
            errorObject.getStatus && errorObject.getStatus() === HTTP_STATUS_400
                ? createAlertConfig(ERROR, _.get(errorObject.responseBody || errorObject, 'message'))
                : errorAlertObject
        );
    };

    const handleConfirmedDelete = () => {
        return deleteWrapper({
            deleteFunction: () => formDeleteService.deleteById(baseUrl, recordId),
            onDeleteGoTo: goToUrl,
            errorHandler: deleteErrorHandler,
        })();
    };

    const handleSubmitFunction = submitWrapper(handleFormSubmit, goToUrl);

    const handleEAPTypeChange = (value) => {
        const isEapTypeTls = value === EAP_TYPE_OPTIONS.eapTls;
        setRequiresAuth(!isEapTypeTls);
        setRequiresCertificate(isEapTypeTls);
        if (isEapTypeTls) {
            setTimeout(() => trigger([USERNAME_FIELD]), 500);
        }
        setRequiresPassword(!isEapTypeTls);
    };

    const dirtyCheck = (isDirty) => {
        return isDirty || agentSelectionIsDirty;
    };

    return (
        <AdminPage
            breadcrumbs={breadcrumbs}
            pageId={buildPageId(functionName)}
            actions={
                <Actions>
                    {showDeleteButton && (
                        <ConfirmDeleteButton handleConfirmedDelete={handleConfirmedDelete} plural={false} />
                    )}
                    <Button onClick={handleSubmit(handleSubmitFunction)} variant="contained" color="primary">
                        <Trans>Save</Trans>
                    </Button>
                </Actions>
            }
        >
            <FormProvider {...methods}>
                <NgpForm
                    submitFunction={handleSubmitFunction}
                    initialized={initialized}
                    loading={finding || saving || deleting}
                    isDirtyFunc={dirtyCheck}
                >
                    <FormSection title={<Trans>Wired Network Details</Trans>}>
                        <NgpTextField
                            fieldName={PROFILE_NAME_FIELD}
                            label={<Trans>Wired Network Name</Trans>}
                            required
                            inputProps={{ size: 32, maxLength: 32 }}
                        />

                        <NgpTextarea
                            fieldName={DESCRIPTION_FIELD}
                            label={<Trans>Description</Trans>}
                            cols={50}
                            inputProps={{ maxLength: 225 }}
                        />

                        <NgpSelect
                            fieldName={EAP_TYPE_FIELD}
                            label={<Trans>EAP Type</Trans>}
                            required
                            onChangeFunc={(e) => handleEAPTypeChange(e?.target?.value, e)}
                        >
                            {_.map(EAP_TYPE_OPTIONS, (value) => (
                                <MenuItem key={value} value={value}>
                                    {value}
                                </MenuItem>
                            ))}
                        </NgpSelect>

                        {requiresCertificate && (
                            <NgpSelect
                                fieldName={AUTH_CERTIFICATE_FIELD}
                                label={<Trans>Default Certificate</Trans>}
                                helpTipText="Applies to all associated nPoints unless overridden."
                            >
                                {_.map(certificates, (certificate) => (
                                    <MenuItem key={certificate[ID_FIELD]} value={certificate[ID_FIELD]}>
                                        {certificate[CERTIFICATE_NAME_FIELD]}
                                    </MenuItem>
                                ))}
                            </NgpSelect>
                        )}
                        <NgpTextField
                            fieldName={USERNAME_FIELD}
                            label={<Trans>Username</Trans>}
                            required={requiresAuth}
                            inputProps={{ size: 65, maxLength: 255 }}
                        />
                        {requiresPassword && (
                            <>
                                <NgpPasswordField
                                    fieldName={PASSWORD_FIELD}
                                    label={<Trans>Password</Trans>}
                                    required={requiresAuth}
                                    inputProps={{ size: 65, maxLength: 255 }}
                                    togglePasswordVisibility
                                />
                            </>
                        )}

                        <NgpCheckbox fieldName={IS_DEFAULT_FIELD} label={<Trans>Default Wired Network</Trans>} />
                    </FormSection>

                    <WiredNetworksAgents formData={formData} setAgentSelectionIsDirty={setAgentSelectionIsDirty} />
                </NgpForm>
            </FormProvider>
        </AdminPage>
    );
}
